import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { ApiError } from '@/shared/services/api-error';
import {
  twoFactorCodeSchema,
  type LoginOutcome,
  type TwoFactorChannel,
  type TwoFactorCodeInput,
  type TwoFactorRequiredOutcome,
} from '../models/auth.model';
import * as authService from '../services/auth.service';

export type TwoFactorStep = 'choose-channel' | 'enter-code';

export interface TwoFactorViewModel {
  step: TwoFactorStep;
  channels: { email: string | null; phone: string | null };
  selectedChannel: TwoFactorChannel | null;
  selectChannel: (channel: TwoFactorChannel) => void;
  sendCode: () => Promise<void>;
  resendCode: () => Promise<void>;
  isSending: boolean;
  /** Destino mascarado para onde o código foi enviado. */
  sentTo: string | null;
  form: UseFormReturn<TwoFactorCodeInput>;
  submit: () => void;
  isVerifying: boolean;
  rememberDevice: boolean;
  setRememberDevice: (remember: boolean) => void;
  error: string | null;
  backToChannels: () => void;
}

/**
 * Estado do segundo fator.
 *
 * São dois passos: escolher o canal e digitar o código. O destino já chega
 * mascarado do servidor — a tela nunca conhece o e-mail ou o telefone completo,
 * porque ela é alcançável com a senha, e a senha pode ter vazado.
 */
export function useTwoFactorViewModel(
  challenge: TwoFactorRequiredOutcome,
  onAuthenticated: (outcome: LoginOutcome) => void,
): TwoFactorViewModel {
  const disponiveis = challenge.channels;
  const unicoCanal: TwoFactorChannel | null = disponiveis.email
    ? disponiveis.phone
      ? null
      : 'email'
    : disponiveis.phone
      ? 'phone'
      : null;

  const [step, setStep] = useState<TwoFactorStep>('choose-channel');
  const [selectedChannel, setSelectedChannel] = useState<TwoFactorChannel | null>(unicoCanal);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<TwoFactorCodeInput>({
    resolver: zodResolver(twoFactorCodeSchema),
    defaultValues: { code: '' },
    mode: 'onBlur',
  });

  const enviar = useCallback(
    async (channel: TwoFactorChannel) => {
      setIsSending(true);
      setError(null);

      try {
        const resultado = await authService.requestTwoFactorCode(challenge.challengeToken, channel);
        setSentTo(resultado.sentTo);
        setStep('enter-code');
      } catch (failure) {
        setError(failure instanceof ApiError ? failure.message : 'Não foi possível enviar o código.');
      } finally {
        setIsSending(false);
      }
    },
    [challenge.challengeToken],
  );

  const sendCode = useCallback(async () => {
    if (!selectedChannel) return;
    await enviar(selectedChannel);
  }, [selectedChannel, enviar]);

  const submit = form.handleSubmit(async ({ code }) => {
    setError(null);

    try {
      onAuthenticated(
        await authService.verifyTwoFactorCode({
          challengeToken: challenge.challengeToken,
          code,
          rememberDevice,
        }),
      );
    } catch (failure) {
      // O código é de uso único: depois de errar, o campo é limpo para não dar a
      // impressão de que basta reenviar o mesmo valor.
      form.setValue('code', '');
      setError(failure instanceof ApiError ? failure.message : 'Não foi possível verificar o código.');
    }
  });

  return {
    step,
    channels: disponiveis,
    selectedChannel,
    selectChannel: setSelectedChannel,
    sendCode,
    resendCode: sendCode,
    isSending,
    sentTo,
    form,
    submit: () => void submit(),
    isVerifying: form.formState.isSubmitting,
    rememberDevice,
    setRememberDevice,
    error,
    backToChannels: () => {
      setStep('choose-channel');
      setSentTo(null);
      setError(null);
      form.reset();
    },
  };
}
